import { formatDateTime } from '../../../utils/dateFormat';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { serviceAgentService, AuditLogDto, DelegationDto } from '../../../services/serviceAgent.service';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import { PersonSpFilterBanner } from '../../../components/PersonSpFilterBanner/PersonSpFilterBanner';
import { investorService, PersonAccountGroup } from '../../../services/investor.service';
import { profileService, ServiceProviderDto } from '../../../services/profile.service';
import './ServiceAgentActivity.scss';

export const ServiceAgentActivity: React.FC = () => {
  const [logs, setLogs]       = useState<AuditLogDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(0);
  const [totalPages, setTotalPages]     = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const PAGE_SIZE = 20;
  // Person/SP filters (same banner as Permissions Center). Attribution: each audit row's
  // delegationId → that delegation's read-time-derived person/spRef. Fail-open — rows
  // without a resolvable delegation are never hidden and count as the login person's.
  const [delegations, setDelegations] = useState<DelegationDto[]>([]);
  const [personGroups, setPersonGroups] = useState<PersonAccountGroup[]>([]);
  const [serviceProviders, setServiceProviders] = useState<ServiceProviderDto[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<number | 'ALL'>('ALL');
  const [selectedSpRef, setSelectedSpRef] = useState<string>('ALL');

  useEffect(() => {
    fetchLogs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedPersonId, selectedSpRef]);

  useEffect(() => {
    // Filter data — best-effort; failures leave the dropdowns at All (nothing hidden).
    serviceAgentService.getMyDelegations().then((d) => setDelegations(d || [])).catch(() => setDelegations([]));
    investorService.getPersonAccounts().then((g) => setPersonGroups(g || [])).catch(() => setPersonGroups([]));
    profileService.getServiceProviders().then((sp) => setServiceProviders(sp || [])).catch(() => setServiceProviders([]));
  }, []);

  // Filtering happens SERVER-SIDE (correct pagination); the delegation join here only
  // labels the Account column.
  const delegationOf = (log: AuditLogDto): DelegationDto | null =>
    log.delegationId ? delegations.find((d) => d.id === log.delegationId) || null : null;
  const accountOf = (log: AuditLogDto): string => {
    const d = delegationOf(log);
    return d?.personName || personGroups[0]?.displayName || '—';
  };
  const visibleLogs = logs;

  const fetchLogs = async (p: number) => {
    setLoading(true);
    try {
      const result = await serviceAgentService.getMyAgentActivity(
        p, PAGE_SIZE,
        selectedPersonId === 'ALL' ? null : selectedPersonId,
        selectedSpRef === 'ALL' ? null : selectedSpRef);
      setLogs(result.content);
      setTotalPages(result.totalPages);
      setTotalElements(result.totalElements);
    } catch (e) {
      // Bare `catch {}` here hid a 404 on a wrong endpoint path for a long time.
      console.error('[ServiceAgentActivity] load failed', e);
      toast.error('Failed to load service agent activity');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso: string) => formatDateTime(iso);

  return (
    <div className="layout-wrapper">
      <main className="container-fluid dashboard-container-main">
        <div className="sa-activity">
          <div className="mb-3">
            <PersonSpFilterBanner
              title="Agent Activity"
              personGroups={personGroups}
              serviceProviders={serviceProviders}
              selectedPersonId={selectedPersonId}
              selectedSpRef={selectedSpRef}
              onPersonChange={(pid) => { setSelectedPersonId(pid); setPage(0); }}
              onSpChange={(ref) => { setSelectedSpRef(ref); setPage(0); }}
            />
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : visibleLogs.length === 0 ? (
            <div className="text-center text-muted py-5">
              {selectedPersonId !== 'ALL' || selectedSpRef !== 'ALL'
                ? 'No activity matches the selected filters.'
                : 'No service agent activity recorded yet.'}
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Date &amp; Time</th>
                      <th>Action</th>
                      <th>Account</th>
                      <th>Category</th>
                      <th>Method</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLogs.map(log => (
                      <tr key={log.id} className={`activity-row ${log.success ? '' : 'activity-row--failed'}`}>
                        <td>
                          <small>{formatDate(log.createdAt)}</small>
                        </td>
                        <td>
                          <span className="action-badge">{log.actionType}</span>
                        </td>
                        <td><small>{accountOf(log)}</small></td>
                        <td>
                          <small className="text-muted">{log.actionCategory || '—'}</small>
                        </td>
                        <td>
                          <code className="small">{log.httpMethod || '—'}</code>
                        </td>
                        <td>
                          {log.success
                            ? <span className="badge bg-success">Success</span>
                            : <span className="badge bg-danger" title={log.errorMessage ?? undefined}>Failed</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="pagination-bar">
                <span>Showing {logs.length} of {totalElements} actions</span>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                  >
                    ← Previous
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ServiceAgentActivity;
